import { AppContext } from '@/App';
import { createContext, FunctionComponent, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import TagManager from 'react-gtm-module';

const ORG_LISTING = 1;

const AnalyticsContext = createContext({});

type AnalyticsPlatform = 'google-analytics-4' | 'google-tag-manager';
type ReportableAnalyticsPlatform = Exclude<AnalyticsPlatform, 'google-tag-manager'>;

interface AnalyticsContextProviderProps {
  analytics: Record<AnalyticsPlatform, any>;
}

export const AnalyticsContextProvider: FunctionComponent<PropsWithChildren<AnalyticsContextProviderProps>> = ({
  children,
  analytics,
}) => {
  const embedPort = useRef<MessagePort | null>(null);
  const queue = useRef([]);
  const baseEventData = useBaseEventData();

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (analytics['google-tag-manager']?.gtm_ids) {
      analytics['google-tag-manager'].gtm_ids.filter(Boolean).forEach((gtmId) => {
        TagManager.initialize({ gtmId });
      });
    }
  }, [analytics]);

  useEffect(() => {
    reportEvent('google-analytics-4')('event', 'orglisting_page_view', baseEventData);
    reportGTM({ event: 'orglisting_page_view', ...baseEventData });
  }, []);

  const onInit = (e) => {
    embedPort.current = e.ports[0];

    embedPort.current.postMessage({
      action: 'setAnalyticsData',
      data: analytics,
    });
  };

  const onPing = (e) => {
    const iframe = document.getElementById('analytics-container') as HTMLIFrameElement;

    const message = 'pong';

    iframe.contentWindow.postMessage(message, e.origin);
  };

  const appendToQueue = (action) => {
    queue.current.push(action);
  };

  const emptyQueue = () => {
    if (!embedPort.current) return;

    queue.current.forEach((action) => {
      embedPort.current.postMessage(action);
    });

    queue.current = [];
  };

  const handleMessage = (e) => {
    if (typeof e.data !== 'string') return;

    if (e.data === 'init') {
      onInit(e);
    }

    if (e.data === 'ping') {
      onPing(e);
    }

    if (e.data === 'postInitPlatform') {
      emptyQueue();
    }
  };

  const getIdsFromConfig = (config) => {
    if (typeof config === 'string') {
      return [config];
    }

    if (Array.isArray(config)) {
      return config;
    }

    if (typeof config === 'object' && config.measurement_ids) {
      return config.measurement_ids;
    }
  };

  const reportEvent =
    (type: ReportableAnalyticsPlatform) =>
    (...args) => {
      const config = analytics[type];

      if (!config) {
        console.warn(`No config found for analytics type: ${type}`);
        return;
      }

      const measurementIds = getIdsFromConfig(config).filter(Boolean);

      const actions = measurementIds.map((measurementId) => ({
        action: 'report',
        platform: type,
        measurementId,
        data: args,
      }));

      if (!embedPort.current) {
        console.log('Analytics is not initialized yet, appending to queue');
        actions.forEach(appendToQueue);
        return;
      }

      actions.forEach((action) => {
        embedPort.current.postMessage(action);
      });
    };

  const reportGTM = (dataLayer) => {
    TagManager.dataLayer({ dataLayer })
  }

  return <AnalyticsContext.Provider value={{ reportEvent, reportGTM }}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsContextProvider');
  }

  return context;
};

export const useBaseEventData = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useBaseEventData must be used within an AppContext');
  }

  // @ts-ignore
  const { listings, type, isCartMode, slug, view } = context.orgListingConfig;

  return useMemo(() => {
    const eventIds = [];
    const eventNames = [];

    if (listings) {
      listings.forEach((listing) => {
        eventIds.push(listing.event_id);
        eventNames.push(listing.listing_title);
      });
    }
    return {
      org_listing_type: type === ORG_LISTING ? 'org listing' : 'managed listing',
      cart_mode: isCartMode,
      listing_slug: slug,
      event_ids: eventIds,
      event_names: eventNames,
      display_type: view,
    };
  }, [type, isCartMode, slug, listings, view]);
};
